import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ShopLayout from './ShopLayout.js';
import { magazineApi } from '../../services/magazineApi.js';
import { subscriptionPlanApi } from '../../services/subscriberApi.js';
import { useCart } from '../../contexts/CartContext.js';
import { toast } from 'react-toastify';
import {
  durationShortLabel,
  formatPkrAmount,
  maxPlanSavingsPct,
  planSavingsPct,
} from '../../utils/planPricing.js';
import { isIssuePurchasable, issuePurchaseBlockedMessage } from '../../utils/issuePurchaseWindow.js';

const FLARE_RED = '#c4151c';
const FLARE_RED_DARK = '#9f1016';

const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

const formatMonth = (month_year) => {
  if (!month_year) return '';
  return month_year;
};

function MagazineCard({ issue }) {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [busy, setBusy] = useState(null);
  const canBuy = isIssuePurchasable(issue);

  const buy = async (delivery_type) => {
    if (!canBuy) {
      toast.info(issuePurchaseBlockedMessage());
      return;
    }
    setBusy(delivery_type);
    try {
      const result = addItem(issue, { delivery_type, quantity: 1 });
      if (result && result.ok === false) {
        toast.info(result.message || issuePurchaseBlockedMessage());
        return;
      }
      toast.success(`${issue.title || issue.month_year || 'Magazine'} (${delivery_type}) added to cart.`);
      navigate('/cart');
    } finally {
      setTimeout(() => setBusy(null), 500);
    }
  };

  return (
    <article className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 flex flex-col">
      <Link to={`/magazines/${issue.slug}`} className="block relative aspect-[3/4] bg-gradient-to-br from-rose-50 via-white to-indigo-50 overflow-hidden">
        {issue.cover_image_url ? (
          <img
            src={issue.cover_image_url}
            alt={issue.title || issue.month_year || 'Flare Magazine cover'}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
            onError={(e) => { e.currentTarget.style.opacity = '0.2'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-rose-300 font-extrabold text-4xl">{formatMonth(issue.month_year)}</span>
          </div>
        )}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold text-white shadow" style={{ backgroundColor: FLARE_RED }}>
            <Icon path="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" className="h-3.5 w-3.5" />
            Published
          </div>
          {issue.is_digital_free && (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-extrabold text-white shadow bg-emerald-500">
              <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" className="h-3.5 w-3.5" />
              FREE DIGITAL
            </div>
          )}
        </div>
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
      </Link>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-baseline justify-between gap-2 mb-1">
          <h3 className="font-extrabold text-gray-900 text-lg leading-tight line-clamp-1">
            {issue.title || formatMonth(issue.month_year) || 'Flare Magazine'}
          </h3>
          <span className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
            {issue.volume ? `Vol ${issue.volume}${issue.issue_no ? ` · Issue ${issue.issue_no}` : ''}` : ''}
          </span>
        </div>
        <div className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: FLARE_RED }}>
          <Icon path="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" className="h-3.5 w-3.5 inline -mt-0.5 mr-1" />
          {formatMonth(issue.month_year)}
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="rounded-xl border p-2.5 bg-emerald-50 border-emerald-200">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5 text-emerald-700">Digital</div>
            {issue.is_digital_free ? (
              <div className="font-extrabold text-emerald-800 flex items-center gap-1">
                <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" className="h-4 w-4" />
                FREE
              </div>
            ) : (
              <div className="font-extrabold text-emerald-900">Rs {(Number(issue.digital_price_pkr) || 0).toLocaleString('en-PK')}</div>
            )}
            <div className="text-[10px] text-emerald-600">PDF · Instant</div>
          </div>
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-2.5">
            <div className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: FLARE_RED }}>Physical</div>
            <div className="font-extrabold" style={{ color: FLARE_RED }}>
              {issue.is_physical_available
                ? `Rs ${(Number(issue.physical_price_pkr) || 0).toLocaleString('en-PK')}`
                : '—'}
            </div>
            <div className="text-[10px] text-rose-600">
              {issue.is_physical_available ? 'Print · Courier' : 'Not Available'}
            </div>
          </div>
        </div>

        <div className="mt-auto space-y-2">
          {issue.is_digital_free && issue.pdf_file_url && (
            <a
              href={issue.pdf_file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-white text-sm font-extrabold shadow-md bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98] transition-all"
            >
              <Icon path="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" className="h-4 w-4" />
              FREE INSTANT DOWNLOAD
            </a>
          )}
          {!canBuy ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-900 leading-snug">
              Archive issue — only last 6 months are available to buy.
            </div>
          ) : null}
          <div className={`grid ${issue.is_digital_free ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
            {!issue.is_digital_free && (
              <button
                onClick={() => buy('Digital')}
                disabled={!canBuy || busy === 'Digital'}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-white text-sm font-bold shadow-sm transition-opacity disabled:opacity-60"
                style={{ backgroundColor: '#059669' }}
              >
                <Icon path="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" className="h-4 w-4" />
                Buy Digital
              </button>
            )}
            <button
              onClick={() => buy('Physical')}
              disabled={!canBuy || busy === 'Physical' || !issue.is_physical_available}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-white text-sm font-bold shadow-sm transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: FLARE_RED }}
            >
              <Icon path="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" className="h-4 w-4" />
              Buy Physical
            </button>
          </div>
          <Link
            to={`/magazines/${issue.slug}`}
            className="block w-full text-center px-3 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-bold hover:bg-gray-50 hover:border-gray-300 transition-colors"
          >
            View Details & Preview
          </Link>
        </div>
      </div>
    </article>
  );
}

function HeroMagazineStack({ issues }) {
  const latest = issues[0];
  return (
    <div className="relative w-full max-w-md mx-auto lg:max-w-none">
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="w-[92%] h-[92%] rounded-3xl -rotate-3"
          style={{ background: 'linear-gradient(135deg, rgba(196,21,28,0.15), rgba(79,70,229,0.12))', boxShadow: '0 25px 60px -15px rgba(196,21,28,0.25)' }}
        />
      </div>
      <div className="relative h-full flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="relative w-full max-w-[360px] aspect-[3/4] group">
          {latest ? (
            <>
              <Link
                to={`/magazines/${latest.slug}`}
                className="block absolute inset-0 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 hover:shadow-[0_30px_80px_-15px_rgba(0,0,0,0.35)] transition-all duration-500 hover:-translate-y-1.5"
                style={{ transform: 'rotate(2deg)' }}
              >
                {latest.cover_image_url ? (
                  <img
                    src={latest.cover_image_url}
                    alt={latest.title || latest.month_year || 'Latest Flare Magazine cover'}
                    className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-rose-50 via-white to-indigo-50 p-8 text-center">
                    <div className="w-20 h-20 rounded-2xl mb-5 flex items-center justify-center" style={{ backgroundColor: FLARE_RED }}>
                      <Icon path="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" className="h-10 w-10 text-white" />
                    </div>
                    <div className="text-rose-400 font-black text-2xl">{formatMonth(latest.month_year) || 'FLARE'}</div>
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />
              </Link>

              <div className="absolute -top-5 -left-5 z-20 flex flex-col gap-2">
                <div className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl text-white text-[11px] font-black uppercase tracking-[0.18em] shadow-[0_8px_20px_-6px_rgba(196,21,28,0.6)] ring-2 ring-white"
                  style={{ backgroundColor: FLARE_RED }}>
                  <Icon path="M13 10V3L4 14h7v7l9-11h-7z" className="h-3.5 w-3.5" />
                  Latest Issue
                </div>
              </div>

              <div className="absolute -top-5 -right-5 z-20 flex flex-col gap-2">
                <div className="inline-flex items-center gap-1.5 px-4 py-2 rounded-2xl bg-white text-gray-900 text-[11px] font-black uppercase tracking-[0.15em] shadow-[0_8px_20px_-6px_rgba(0,0,0,0.15)] ring-2 border border-gray-100">
                  <Icon path="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" className="h-3.5 w-3.5" style={{ color: FLARE_RED }} />
                  {formatMonth(latest.month_year) || 'Current Month'}
                </div>
              </div>

              <div className="absolute -bottom-4 inset-x-0 z-20 px-4">
                <div className="bg-white rounded-2xl shadow-[0_15px_40px_-10px_rgba(0,0,0,0.2)] border border-gray-100 p-4">
                  <div className="flex items-baseline justify-between gap-2 mb-1.5">
                    <h3 className="font-black text-gray-900 text-base leading-tight truncate">
                      {latest.title || formatMonth(latest.month_year) || 'Flare Magazine'}
                    </h3>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex-shrink-0">
                      {latest.volume ? `Vol ${latest.volume}${latest.issue_no ? ` · I${latest.issue_no}` : ''}` : ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-2">
                      <div className="text-[9px] font-black uppercase tracking-wider text-emerald-700 mb-0.5">Digital</div>
                      <div className="font-black text-emerald-800 text-sm">
                        {latest.is_digital_free ? 'FREE' : `Rs ${(Number(latest.digital_price_pkr) || 0).toLocaleString('en-PK')}`}
                      </div>
                    </div>
                    <div className="rounded-xl bg-rose-50 border border-rose-200 p-2">
                      <div className="text-[9px] font-black uppercase tracking-wider mb-0.5" style={{ color: FLARE_RED }}>Physical</div>
                      <div className="font-black text-sm" style={{ color: FLARE_RED }}>
                        {latest.is_physical_available
                          ? `Rs ${(Number(latest.physical_price_pkr) || 0).toLocaleString('en-PK')}`
                          : '—'}
                      </div>
                    </div>
                  </div>
                  <Link
                    to={`/magazines/${latest.slug}`}
                    className="mt-3 flex items-center justify-center gap-1.5 w-full px-3 py-2.5 rounded-xl text-white text-sm font-black shadow-md hover:shadow-lg transition-shadow"
                    style={{ backgroundColor: FLARE_RED }}
                  >
                    <Icon path="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" className="h-4 w-4" />
                    Read Now
                  </Link>
                </div>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-rose-50 via-white to-indigo-50 flex items-center justify-center shadow-2xl">
              <div className="text-center p-8">
                <div className="w-20 h-20 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: FLARE_RED }}>
                  <Icon path="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" className="h-10 w-10 text-white" />
                </div>
                <div className="font-black text-gray-800 text-lg">Flare Magazine</div>
                <div className="text-sm text-gray-500 font-semibold mt-1">Latest issues coming soon</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FeaturesSection() {
  const features = [
    {
      icon: "M13 10V3L4 14h7v7l9-11h-7z",
      title: "Instant Digital Access",
      desc: "Buy once, download forever. Your PDFs are available instantly — no waiting, no shipping delays.",
      color: "#059669",
      bg: "#ecfdf5",
    },
    {
      icon: "M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4",
      title: "Printed Copies Delivered",
      desc: "Premium quality printed issues delivered via courier to your doorstep all across Pakistan.",
      color: FLARE_RED,
      bg: "#fff1f2",
    },
    {
      icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
      title: "Secure Checkout",
      desc: "100% secure payment gateways. JazzCash, EasyPaisa, and bank transfers — all encrypted and protected.",
      color: "#7c3aed",
      bg: "#f5f3ff",
    },
    {
      icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
      title: "Monthly Subscription Plans",
      desc: "Save big with 3, 6, and 12-month plans. Automatic delivery and exclusive subscriber perks included.",
      color: "#0891b2",
      bg: "#ecfeff",
    },
    {
      icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z",
      title: "10,000+ Readers",
      desc: "Pakistan's fastest-growing magazine community. Trusted by readers in Lahore, Karachi, Islamabad & beyond.",
      color: "#d97706",
      bg: "#fffbeb",
    },
    {
      icon: "M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172A5 5 0 008 12c0 1.396.584 2.66 1.515 3.536m6.73-8.51A22.01 22.01 0 0119 12a21.95 21.95 0 01-2.578 9.819M5.636 18.364A9 9 0 013 12c0-2.35.9-4.494 2.36-6.1",
      title: "24/7 Support",
      desc: "Questions about orders, delivery, or digital access? Our team is here around the clock to help you out.",
      color: "#db2777",
      bg: "#fdf2f8",
    },
  ];

  return (
    <section id="features" className="relative py-16 lg:py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs font-black uppercase tracking-[0.25em] mb-3" style={{ color: FLARE_RED }}>
            Why Flare Magazine
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 tracking-tight mb-4">
            Everything you love,<br />
            <span style={{ color: FLARE_RED }}>all in one place.</span>
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            From exclusive interviews to premium print quality, Flare is built for readers who want the best.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {features.map((f, i) => (
            <div
              key={i}
              className="group relative bg-white rounded-2xl border border-gray-100 p-6 lg:p-7 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform"
                style={{ backgroundColor: f.bg }}
              >
                <Icon
                  path={f.icon}
                  className="h-6 w-6"
                  style={{ color: f.color }}
                />
              </div>
              <h3 className="font-black text-gray-900 text-lg mb-2">
                {f.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: "01", title: "Browse Issues", desc: "Explore our latest and archive issues — covers, previews, and exclusive content." , icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" },
    { n: "02", title: "Choose Your Format", desc: "Pick instant digital download or premium printed copy — or get both.", icon: "M4 6h16M4 10h16M4 14h16M4 18h16" },
    { n: "03", title: "Secure Payment", desc: "Pay securely with JazzCash, EasyPaisa, bank transfer, or card.", icon: "M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" },
    { n: "04", title: "Enjoy Reading", desc: "Instant PDF delivery or printed copy shipped to your door within days.", icon: "M5 13l4 4L19 7" },
  ];
  return (
    <section id="how" className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs font-black uppercase tracking-[0.25em] mb-3" style={{ color: FLARE_RED }}>
            How it Works
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 tracking-tight mb-4">
            Get your copy in <span style={{ color: FLARE_RED }}>4 simple steps</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 relative">
          <div className="hidden lg:block absolute top-14 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-transparent via-rose-200 to-transparent" />
          {steps.map((s, i) => (
            <div key={i} className="relative text-center">
              <div className="relative w-28 h-28 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-rose-100 via-white to-indigo-50 shadow-[0_10px_25px_-5px_rgba(196,21,28,0.15)]" />
                <div className="absolute inset-2 rounded-full flex items-center justify-center">
                  <Icon path={s.icon} className="h-8 w-8" style={{ color: FLARE_RED }} />
                </div>
                <div className="absolute -top-2 -right-2 w-9 h-9 rounded-full flex items-center justify-center font-black text-white shadow-lg ring-4 ring-white" style={{ backgroundColor: FLARE_RED }}>
                  {s.n}
                </div>
              </div>
              <h3 className="font-black text-gray-900 text-xl mb-2">{s.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await subscriptionPlanApi.getActive();
        const list = r?.data?.subscription_plans || r?.data?.plans || [];
        if (!cancelled && Array.isArray(list)) setPlans(list);
      } catch {
        /* keep empty — heading falls back */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const maxSave = maxPlanSavingsPct(plans) || 40;
  const preferred = [1, 3, 12, 24];
  const byMonth = new Map(plans.map((p) => [Number(p.duration_months), p]));
  const picked = preferred.map((m) => byMonth.get(m)).filter(Boolean);
  const tiles = (picked.length ? picked : plans).slice(0, 4).map((plan) => {
    const pct = planSavingsPct(plan);
    const months = Number(plan.duration_months) || 1;
    return {
      label: durationShortLabel(months),
      price: pct > 0 ? `Save ${pct}%` : `From ${formatPkrAmount(plan.price_pkr)}`,
    };
  });
  const fallbackTiles = [
    { label: 'Monthly', price: 'From Rs 1,500' },
    { label: 'Quarterly', price: 'Save 10%' },
    { label: 'Yearly', price: 'Save 33%' },
    { label: 'Biennial', price: 'Save 40%' },
  ];
  const shown = tiles.length ? tiles : fallbackTiles;
  const icons = [
    'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
    'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
    'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z',
  ];

  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="relative rounded-3xl overflow-hidden px-6 sm:px-10 lg:px-14 py-12 lg:py-16" style={{ background: `linear-gradient(135deg, ${FLARE_RED} 0%, #7f1d1d 100%)` }}>
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-20 bg-white blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-16 w-80 h-80 rounded-full opacity-10 bg-white blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur text-white/90 text-xs font-black uppercase tracking-widest mb-5 border border-white/20">
                <Icon path="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" className="h-3.5 w-3.5" />
                Limited Time Offer
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1] mb-4">
                Save up to {maxSave}% with<br />a longer plan
              </h2>
              <p className="text-white/80 text-base sm:text-lg max-w-lg mb-7 leading-relaxed">
                Subscribe to a full year of Flare and unlock free home delivery, digital access, exclusive content, and members-only events.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/plans"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-black text-sm text-gray-900 bg-white shadow-xl hover:scale-[1.02] active:scale-[0.99] transition-transform"
                >
                  View Subscription Plans
                  <Icon path="M14 5l7 7m0 0l-7 7m7-7H3" className="h-5 w-5" />
                </Link>
                <Link
                  to="/magazines"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-black text-sm text-white bg-white/10 hover:bg-white/15 backdrop-blur border border-white/20 transition-colors"
                >
                  Shop Single Issues
                </Link>
              </div>
            </div>
            <div className="hidden lg:flex justify-end">
              <div className="grid grid-cols-2 gap-4">
                {shown.map((p, i) => (
                  <div key={p.label} className="rounded-2xl border border-white/15 bg-white/5 backdrop-blur p-5 hover:bg-white/10 transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center mb-3">
                      <Icon path={icons[i % icons.length]} className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-white/70 text-[11px] font-bold uppercase tracking-widest mb-1">{p.label}</div>
                    <div className="text-white font-black text-xl">{p.price}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ShopHomeSection({ listOnly = false }) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const PER_PAGE = 12;
  const CURRENT_YEAR = new Date().getFullYear();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await magazineApi.getPublishedIssues({ per_page: 200 });
        if (cancelled) return;
        let list = Array.isArray(r?.data?.data_formatted) ? r.data.data_formatted : (
          Array.isArray(r?.data?.data) ? r.data.data.map(magazineApi.formatIssue) : (
            Array.isArray(r?.data) ? r.data.map(magazineApi.formatIssue) : []
          )
        );
        const extractYear = (it) => {
          for (const f of ['publish_date', 'month_year', 'created_at']) {
            const s = (it[f] || '').toString();
            if (!s) continue;
            const m = s.match(/(19|20)\d{2}/);
            if (m) return parseInt(m[0], 10);
          }
          return 0;
        };
        if (!listOnly) {
          list = list.filter((it) => extractYear(it) === CURRENT_YEAR);
        }
        list = [...list].sort((a, b) => {
          const ds = (str) => {
            if (!str) return 0;
            const d = new Date(str);
            return isNaN(d.getTime()) ? 0 : d.getTime();
          };
          const by = a.publish_date || a.month_year || a.created_at || '';
          const cy = b.publish_date || b.month_year || b.created_at || '';
          const diff = ds(cy) - ds(by);
          if (diff !== 0) return diff;
          return (b.id || 0) - (a.id || 0);
        });
        setIssues(list);
        setPage(1);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load magazines.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [listOnly, CURRENT_YEAR]);

  const totalPages = Math.max(1, Math.ceil(issues.length / PER_PAGE));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const startIdx = (safePage - 1) * PER_PAGE;
  const endIdx = startIdx + PER_PAGE;
  const pageItems = issues.slice(startIdx, endIdx);

  const goTo = (p) => {
    const target = Math.min(Math.max(1, p), totalPages);
    setPage(target);
    const el = document.getElementById('magazine-grid');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <ShopLayout>
      {!listOnly && (
        <section className="relative overflow-hidden" style={{ background: `linear-gradient(135deg, #fff1f2 0%, #ffffff 45%, #eef2ff 100%)` }}>
          <div className="absolute top-0 right-0 -mr-32 -mt-32 w-96 h-96 rounded-full opacity-30 pointer-events-none" style={{ backgroundColor: FLARE_RED, filter: 'blur(100px)' }} />
          <div className="absolute bottom-0 left-0 -ml-24 -mb-24 w-80 h-80 rounded-full opacity-20 pointer-events-none" style={{ backgroundColor: '#6366f1', filter: 'blur(90px)' }} />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-20 lg:pb-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black uppercase tracking-[0.2em] mb-6 shadow-sm" style={{ backgroundColor: '#ffffff', color: FLARE_RED, border: '1px solid rgba(196,21,28,0.12)' }}>
                  <Icon path="M13 10V3L4 14h7v7l9-11h-7z" className="h-3.5 w-3.5" />
                  New issues drop every month
                </span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.02] tracking-tight text-gray-900 mb-6">
                  Pakistan's best read.
                  <br />
                  <span style={{ color: FLARE_RED }}>Delivered your way.</span>
                </h1>
                <p className="text-base sm:text-lg text-gray-600 max-w-xl mb-9 leading-relaxed">
                  Flare Magazine brings you exclusive interviews, lifestyle trends, entertainment, politics, and culture every single month. Choose instant digital access or premium printed copies — same great content, your favorite format.
                </p>
                <div className="flex flex-wrap items-center gap-3 sm:gap-4">
                  <a
                    href="#magazine-grid"
                    className="inline-flex items-center gap-2.5 px-7 py-4 rounded-xl font-black text-white shadow-xl transition-all hover:-translate-y-0.5 hover:shadow-2xl"
                    style={{ backgroundColor: FLARE_RED, boxShadow: '0 12px 28px -10px rgba(196,21,28,0.65)' }}
                  >
                    Shop Latest Issues
                    <Icon path="M14 5l7 7m0 0l-7 7m7-7H3" className="h-5 w-5" />
                  </a>
                  <Link
                    to="/plans"
                    className="inline-flex items-center gap-2.5 px-7 py-4 rounded-xl font-black text-gray-800 bg-white border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    View Subscriptions
                  </Link>
                </div>
                <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg">
                  <div>
                    <div className="text-3xl sm:text-4xl font-black text-gray-900">{issues.length || '—'}</div>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Issues Published</div>
                  </div>
                  <div>
                    <div className="text-3xl sm:text-4xl font-black text-gray-900">2</div>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Easy Formats</div>
                  </div>
                  <div>
                    <div className="text-3xl sm:text-4xl font-black text-gray-900">24/7</div>
                    <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">Readers Support</div>
                  </div>
                </div>
                <div className="mt-10 flex items-center gap-5">
                  <div className="flex -space-x-3">
                    {[0,1,2,3].map(i => (
                      <div key={i} className="w-10 h-10 rounded-full ring-2 ring-white border-2 border-white flex items-center justify-center font-black text-white text-xs shadow-md"
                        style={{ backgroundColor: [FLARE_RED, '#7c3aed', '#059669', '#d97706'][i] }}>
                        {['A','S','R','M'][i]}
                      </div>
                    ))}
                  </div>
                  <div>
                    <div className="flex items-center gap-0.5 mb-0.5">
                      {[0,1,2,3,4].map(i => (
                        <svg key={i} viewBox="0 0 20 20" fill="#f59e0b" className="w-4 h-4"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                      ))}
                    </div>
                    <div className="text-xs text-gray-500 font-bold">
                      <span className="text-gray-900">10,000+</span> happy readers
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block relative">
                <HeroMagazineStack issues={issues} />
              </div>
            </div>
          </div>
        </section>
      )}

      {!listOnly && <FeaturesSection />}
      {!listOnly && <HowItWorks />}

      <section id="magazine-grid" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.25em] mb-2" style={{ color: FLARE_RED }}>
              <Icon path="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" className="h-3.5 w-3.5 inline -mt-0.5 mr-1.5" />
              {listOnly ? 'Full Library' : 'Magazine Shop'}
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 tracking-tight">
              {listOnly ? 'All Magazine Issues' : 'Featured Monthly Issues'}
            </h2>
            <p className="mt-3 text-gray-600 max-w-2xl text-base">
              Browse every published Flare Magazine issue. Buy digital PDFs for instant download, or printed copies delivered across Pakistan.
            </p>
          </div>
          {!listOnly && (
            <Link
              to="/magazines"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-black text-sm border border-gray-200 bg-white text-gray-800 hover:bg-gray-50 hover:border-gray-300 transition-colors shadow-sm"
            >
              View All Issues
              <Icon path="M14 5l7 7m0 0l-7 7m7-7H3" className="h-4 w-4" />
            </Link>
          )}
          <div className="inline-flex items-center gap-2 px-4 py-3 rounded-xl bg-white border border-gray-200 shadow-sm text-sm">
            <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" className="h-4 w-4" style={{ color: '#059669' }} />
            <span className="font-bold text-gray-700">{issues.length} issues · Secure Checkout</span>
          </div>
        </div>

        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-gray-100 bg-white">
                <div className="aspect-[3/4] bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 animate-pulse" />
                <div className="p-4 space-y-3">
                  <div className="h-5 bg-gray-200 rounded animate-pulse w-3/4" />
                  <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
                  <div className="h-16 bg-gray-100 rounded-xl animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="p-6 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-sm font-bold">
            Couldn't load magazines: {error}. Please try again later.
          </div>
        )}

        {!loading && !error && issues.length === 0 && (
          <div className="p-12 rounded-2xl bg-white border border-dashed border-gray-300 text-center text-gray-500">
            No published issues yet. Check back soon!
          </div>
        )}

        {!loading && issues.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {pageItems.map(issue => (
                <MagazineCard key={issue.id} issue={issue} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm font-bold text-gray-500">
                  Showing <span className="text-gray-900">{startIdx + 1}-{Math.min(endIdx, issues.length)}</span> of <span className="text-gray-900">{issues.length}</span>{' '}
                  {CURRENT_YEAR} issues
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => goTo(safePage - 1)}
                    disabled={safePage <= 1}
                    className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Previous page"
                  >
                    <Icon path="M15 19l-7-7 7-7" className="h-4 w-4" />
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => {
                    const p = i + 1;
                    const showWindow = totalPages <= 7 || (
                      p === 1 || p === totalPages || (Math.abs(p - safePage) <= 2)
                    );
                    const isEllipsis = !showWindow && (
                      (p === 2 && safePage > 5) || (p === totalPages - 1 && safePage < totalPages - 4)
                    );
                    if (!showWindow && !isEllipsis) return null;
                    if (isEllipsis) {
                      return (
                        <div key={`e-${p}`} className="w-10 h-10 flex items-center justify-center text-gray-400 font-bold">
                          …
                        </div>
                      );
                    }
                    return (
                      <button
                        key={p}
                        onClick={() => goTo(p)}
                        className={`min-w-[40px] h-10 px-3 rounded-lg inline-flex items-center justify-center font-black text-sm transition-all ${
                          p === safePage
                            ? 'text-white shadow-md'
                            : 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                        style={p === safePage ? { backgroundColor: FLARE_RED } : {}}
                      >
                        {p}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => goTo(safePage + 1)}
                    disabled={safePage >= totalPages}
                    className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    aria-label="Next page"
                  >
                    <Icon path="M9 5l7 7-7 7" className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {!listOnly && <CTASection />}
    </ShopLayout>
  );
}
